"use client";

import type { AiTimelineWithTrends } from "@saas/ai-analytics/lib/api";
import { Card, CardContent } from "@ui/components/card";
import { deltaTone } from "@ui/lib/chart-palette";
import {
	BotIcon,
	TrendingDownIcon,
	TrendingUpIcon,
	UserIcon,
} from "lucide-react";

interface AiSummaryWithTrendsProps {
	data: AiTimelineWithTrends;
	timeRangeLabel: string; // e.g., "this week", "last 7 days"
}

export function AiSummaryWithTrends({
	data,
	timeRangeLabel,
}: AiSummaryWithTrendsProps) {
	const formatTrend = (trend: number) => {
		const isPositive = trend >= 0;
		const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;
		const tone = deltaTone(trend);
		const color =
			tone === "positive"
				? "text-positive"
				: tone === "negative"
					? "text-negative"
					: "text-neutral";

		return (
			<div className={`flex items-center gap-1 text-sm ${color}`}>
				<Icon className="h-4 w-4" />
				<span>{Math.abs(trend).toFixed(1)}%</span>
			</div>
		);
	};

	const formatTrendText = (
		current: number,
		previous: number,
		trend: number,
		type: "AI visits" | "human visits",
	) => {
		const isPositive = trend >= 0;
		const direction = isPositive ? "more" : "fewer";
		const comparisonPeriod = timeRangeLabel
			.replace("this", "last")
			.replace("Last", "Previous");
		const difference = current - previous;
		return `${difference.toLocaleString()} ${direction} than ${comparisonPeriod} (${previous.toLocaleString()})`;
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			{/* AI Bot Visits */}
			<Card>
				<CardContent className="p-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<BotIcon className="h-5 w-5 text-muted-foreground" />
							<div>
								<div className="text-2xl font-bold">
									{data.currentPeriod.totalBotVisits.toLocaleString()}
								</div>
								<div className="text-sm text-muted-foreground">
									AI Bot Visits
								</div>
							</div>
						</div>
						{data.previousPeriod.totalBotVisits > 0 &&
							formatTrend(data.trends.botVisitsTrend)}
					</div>
					{data.previousPeriod.totalBotVisits > 0 && (
						<div className="text-xs text-muted-foreground mt-2">
							{formatTrendText(
								data.currentPeriod.totalBotVisits,
								data.previousPeriod.totalBotVisits,
								data.trends.botVisitsTrend,
								"AI visits",
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Human Visits */}
			<Card>
				<CardContent className="p-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<UserIcon className="h-5 w-5 text-muted-foreground" />
							<div>
								<div className="text-2xl font-bold">
									{data.currentPeriod.totalHumanVisits.toLocaleString()}
								</div>
								<div className="text-sm text-muted-foreground">
									AI-Referred Visits
								</div>
							</div>
						</div>
						{data.previousPeriod.totalHumanVisits > 0 &&
							formatTrend(data.trends.humanVisitsTrend)}
					</div>
					{data.previousPeriod.totalHumanVisits > 0 && (
						<div className="text-xs text-muted-foreground mt-2">
							{formatTrendText(
								data.currentPeriod.totalHumanVisits,
								data.previousPeriod.totalHumanVisits,
								data.trends.humanVisitsTrend,
								"human visits",
							)}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
